import React from "react";
import { ExternalLink, Link, Section } from "@sscs/ui";
import { Dialog } from "Components";
import { DOCS_URL } from "Constants";

type WhatsNewProps = {
  isOpen: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const WhatsNew = ({
  isOpen,
  onConfirm,
  onClose,
}: WhatsNewProps): React.JSX.Element => {
  return (
    <Dialog
      maxWidth="md"
      title="Major features recently added to Spectra Assure"
      isOpen={isOpen}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={onConfirm}
      confirmText="OK"
      showCancel={false}
      meta={
        <ExternalLink href={`${DOCS_URL}/release-notes`}>
          Release notes
        </ExternalLink>
      }
    >
      <Section>
        <Section.Title>Visual Studio Code IDE</Section.Title>
        <div>
          The new{" "}
          <Link
            href="https://docs.secure.software/community/spectra-code"
            target="_blank"
            rel="noreferrer"
            inline
          >
            Spectra Code
          </Link>{" "}
          extension for VS Code checks declared dependencies in manifest and
          lock files against{" "}
          <Link
            href="https://secure.software/"
            target="_blank"
            rel="noreferrer"
            inline
          >
            Spectra Assure Community
          </Link>
          , showing found issues, including detected malware and
          vulnerabilities, and allowing developers to remediate risky packages
          to safe versions with one click.
        </div>
      </Section>

      <Section>
        <Section.Title>Software Catalog</Section.Title>
        <div>
          The Software Catalog is a centralized, secure repository where users
          can browse and download pre-approved software. Built for total
          control, administrators can publish, manage, or instantly revoke
          software to safeguard the organization. To save time, administrators
          can also use AI features to auto-generate package overviews and
          software information. AI-assisted features are disabled by default and
          must be enabled by an administrator. [New] Software Catalog features
          are also available via public API.
        </div>
      </Section>

      <Section>
        <Section.Title>Filestream API</Section.Title>
        <div>
          Users can now upload or import files into a group’s Filestream for
          analysis via public APIs. Additionally, these APIs allow users to list
          all Filestream files in a group, check their analysis status, download
          files, manage approvals, trigger re-scans, delete files, and export
          comprehensive analysis reports. Files uploaded to Filestream are still
          retained for up to 90 days and automatically deleted upon expiration.
        </div>
      </Section>

      <Section>
        <Section.Title>P/URL Import Updates</Section.Title>
        <div>
          Users can now{" "}
          <Link
            href="https://docs.secure.software/portal/filestream#file-import"
            target="_blank"
            rel="noreferrer"
            inline
          >
            import
          </Link>{" "}
          open-source packages from Maven repo, ML models directly from public
          or private HuggingFace repositories, and [New] Docker Hardened Images
          from Docker Hub.
        </div>
      </Section>

      <Section>
        <Section.Title>Artifactory integration</Section.Title>
        <div>
          Users can now protect the software supply chain by integrating JFrog
          Artifactory with Spectra Assure Community.{" "}
          <Link
            href="https://github.com/reversinglabs/rlArtifactoryPlugin"
            target="_blank"
            rel="noreferrer"
            inline
          >
            Artifactory plugin
          </Link>{" "}
          automatically detects and blocks malicious or risky PyPI and npm
          open-source dependencies even before developers download them.
        </div>
      </Section>
    </Dialog>
  );
};

export default WhatsNew;
